import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { observer } from 'mobx-react-lite'
import { applyDevTools } from 'prosemirror-dev-toolkit'
import { EditorState, NodeSelection, TextSelection, Transaction } from 'prosemirror-state'
import { EditorProps, EditorView } from 'prosemirror-view'
import { useProseInstance } from 'providers/ProseInstanceProvider'
import { forwardRef, ReactNode, useContext, useEffect, useRef, useState } from 'react'
import { ViewEditableContext } from 'screens/ProseEdit/components/ViewEditableContextProvider/ViewEditableContextProvider'
import { MAIN_CONTENT_ID } from 'screens/ProseEdit/Constants'
import { AlmanacEditorState, AlmanacEditorStateContext } from 'stores/EditorState'
import { Layer } from 'types'
import { reportError } from 'utils/error'
import { useLayerStates } from '../../../screens/ProseEdit/components/LayerStatesProvider/LayerStatesProvider'
import { tryGetTrackChangesState } from '../Plugins/TrackChanges/constants'
import { trackTransaction } from '../Plugins/TrackChanges/trackTransaction'
import { patchEditorFlushForSafari } from '../utils/editorHacks'
import { EditorDocProvider, EditorStateProvider, EditorViewProvider } from './EditorProviderContext'
import { useDocStore } from 'stores/DocStore'
import { DrawerWrapper } from 'components/Drawer/DrawerWrapper'

interface Props {
  freeze?: (transaction: Transaction) => boolean
  onTransaction: (tx: Transaction, updatedState: EditorState, layerId: string) => void
  editorProps?: EditorProps
  children: ReactNode
  onViewUpdated?: (view: EditorView) => void
  userId?: number
  internalRef?: any
  currentLayer: Layer
}

export const EditorProvider = observer(
  forwardRef(
    (
      { onTransaction, editorProps, children, onViewUpdated, freeze = () => false, userId, currentLayer }: Props,
      editorRef,
    ) => {
      const { updateView: updateContextView } = useProseInstance()
      const { getLayerState, setLayerState, currentLayerState } = useLayerStates()
      const { isMobile } = useResponsiveDesign()
      const docStore = useDocStore()
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { editable, setEditable } = useContext(ViewEditableContext)!

      const [{ view, editorStateStore }, setViewAndAlmanacState] = useState(() => {
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const state = getLayerState(currentLayer.id)!
        const newEditorView = new EditorView(undefined, {
          ...editorProps,
          state,
          handleScrollToSelection: (view) => {
            const editorContainsSelection = view.dom.contains(view.dom.ownerDocument.getSelection()?.focusNode ?? null)
            /**
             * Prosemirror will use the window's real selection to determine where to scroll to, as opposed to using
             * view.selection
             * This is a problem for us because at the point of inserting a new node, the real selection will typically
             * be inside a button or modal or somesuch, and the scroll will fail.
             * We therefore take over the scroll behavior here, and scroll based off of the prosemirror selection
             */
            if (view.state.selection instanceof TextSelection || view.state.selection instanceof NodeSelection) {
              const scrollElement = document.getElementById(MAIN_CONTENT_ID)
              const domElement = view.domAtPos(view.state.selection.from).node
              if (scrollElement instanceof HTMLElement && domElement && domElement instanceof Element) {
                // We got the editor itself
                if (domElement?.classList.contains('ProseMirror')) return false

                if (isMobile) {
                  const TOP_TOOLBAR_HEIGHT = 50
                  const BOTTOM_FORMAT_BAR_HEIGHT = 46
                  const position = domElement.getBoundingClientRect()
                  const viewportHeight = window.visualViewport?.height || 0

                  // only if the element is not in view
                  const selectionIsAboveTopToolBar = position.top < TOP_TOOLBAR_HEIGHT
                  const selectionIsBelowBottomFormatBar =
                    position.bottom > (viewportHeight || 0) - BOTTOM_FORMAT_BAR_HEIGHT
                  const outsideView = selectionIsBelowBottomFormatBar || selectionIsAboveTopToolBar

                  // scroll as needed
                  if (outsideView) {
                    domElement.scrollIntoView({
                      behavior: 'instant',
                      block: 'center',
                      inline: 'center',
                    })
                    return true
                  }
                }

                const position = view.coordsAtPos(view.state.selection.head)
                const scrollElementRect = scrollElement.getBoundingClientRect()
                // only if the element is not in view
                const outsideView = position.top < scrollElementRect.top || position.bottom > scrollElementRect.bottom
                // if the native (browser) selection is inside the editor, we can bail and allow prosemirror to handle it
                const unhandledOutsideView = !editorContainsSelection && outsideView
                // autoscroll with a padding when typing near the bottom of the doc
                const shouldBottomScroll =
                  editorContainsSelection && !isMobile && scrollElementRect.bottom - position.bottom < 150
                // scroll as needed
                if (unhandledOutsideView || shouldBottomScroll) {
                  const top = 150 - (scrollElementRect.bottom - position.bottom)
                  scrollElement.scrollBy({ top, behavior: 'smooth' })
                  return true
                }
              }
            }

            return false
          },
          dispatchTransaction(originalTr) {
            if (freeze(originalTr)) return
            let tr = originalTr
            const trackingChanges = tryGetTrackChangesState(this.state)?.store.trackingChanges
            if (trackingChanges && userId) {
              tr = trackTransaction(originalTr, this.state, userId)
            }

            const updatedState = this.state.apply(tr)

            const view = this as any as EditorView
            // EDI-1763 - Debug iOS Chrome Prod Only problem with virtual keyboard stuck in its expanded mode
            ;(window as any).editorView = view

            // check if view is mounted, otherwise it might try to update an unmounted view   s
            // causing jest to bark
            if ((view as any).docView) {
              view.updateState(updatedState)
              setLayerState(currentLayer.id, updatedState)
              onTransaction(tr, updatedState, currentLayer.id)
              try {
                almanacEditorstate.updateFromView(view)
              } catch (e) {
                reportError(e)
              }
            }
          },
        })
        patchEditorFlushForSafari(newEditorView)

        if (editorRef) {
          //@ts-ignore
          editorRef.current = newEditorView
        }
        const almanacEditorstate = new AlmanacEditorState(newEditorView)
        return { view: newEditorView, editorStateStore: almanacEditorstate }
      })

      const firstRender = useRef(true)
      useEffect(() => {
        if (firstRender.current) {
          firstRender.current = false
          return
        }

        if (currentLayer) {
          const state = getLayerState(currentLayer.id)

          if (!state) {
            return
          }

          const newView = new EditorView(undefined, {
            ...editorProps,
            state,
            dispatchTransaction: function (originalTr) {
              if (freeze(originalTr)) return
              let tr = originalTr
              const trackingChanges = tryGetTrackChangesState(this.state)?.store.trackingChanges
              if (trackingChanges && userId) {
                tr = trackTransaction(originalTr, this.state, userId)
              }

              const updatedState = this.state.apply(tr)

              const view = this as any as EditorView

              // check if view is mounted, otherwise it might try to update an unmounted view   s
              // causing jest to bark
              if ((view as any).docView) {
                view.updateState(updatedState)
                setLayerState(currentLayer.id, updatedState)
                onTransaction(tr, updatedState, currentLayer.id)
                try {
                  almanacEditorState.updateFromView(view)
                } catch (e) {
                  reportError(e)
                }
              }
            },
          })
          patchEditorFlushForSafari(newView)

          if (editorRef) {
            //@ts-ignore
            editorRef.current = newView
          }
          const almanacEditorState = new AlmanacEditorState(newView)
          setViewAndAlmanacState({ view: newView, editorStateStore: almanacEditorState })
        }
      }, [currentLayer.id]) // eslint-disable-line react-hooks/exhaustive-deps

      useEffect(() => {
        if (editorProps?.editable)
          view.setProps({
            editable: (...args) => {
              // TODO: Fix this the next time the file is edited.
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              const isEditable = editorProps.editable!(...args)

              if (editable !== isEditable) {
                setEditable(isEditable)
              }
              return isEditable
            },
          })
      }, [view, editorProps?.editable, editable, setEditable]) // eslint-disable-line react-hooks/exhaustive-deps

      useEffect(() => {
        updateContextView(view)
        return () => {
          updateContextView()
        }
      }, [view, updateContextView])

      useEffect(() => {
        onViewUpdated && onViewUpdated(view)
      }, [view, onViewUpdated])

      useEffect(() => {
        process.env.NODE_ENV === 'development' && !isMobile && applyDevTools(view)
      }, [view, isMobile])

      useEffect(() => {
        return () => {
          view.destroy()
        }
      }, [view])

      useEffect(() => {
        if (editorStateStore?.allNodeTypes.has(editorStateStore.view.state.schema.nodes.ai_suggestion)) {
          docStore.setIsGeneratingSuggestion(true)
        } else {
          docStore.setIsGeneratingSuggestion(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [editorStateStore?.allNodeTypes.has(editorStateStore.view.state.schema.nodes.ai_suggestion)])

      return (
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        <EditorDocProvider value={currentLayerState!.doc}>
          <EditorStateProvider value={currentLayerState}>
            <AlmanacEditorStateContext.Provider value={editorStateStore}>
              <EditorViewProvider value={view}>
                <DrawerWrapper />
                {children}
              </EditorViewProvider>
            </AlmanacEditorStateContext.Provider>
          </EditorStateProvider>
        </EditorDocProvider>
      )
    },
  ),
)
